import axios from "axios";
import { dedupeWrites } from "./writeGuards";

const http = axios.create({
  withCredentials: true,
});

// Двойное нажатие «Сохранить» не задваивает запись (writeGuards.js).
dedupeWrites(http);

const SUBSCRIPTION_REDIRECT_MESSAGE = "Подписка истекла. Оплатите подписку.";

const SUBSCRIPTION_FREE_ROUTES = [
  "/wildberries/planUnit",
  "/ozon/planUnit",
  "/referral",
  "/payment",
  "/mydata",
  "/sellertnewuserpage",
  "/addnewcompany",
  "/admin",
  "/adminPage",
];

let csrfToken = null;
let csrfPromise = null;

function isSubscriptionFreeRoute() {
  if (typeof window === "undefined") return false;

  const pathname = window.location?.pathname || "";
  return SUBSCRIPTION_FREE_ROUTES.some(
    (route) => pathname === route || pathname.startsWith(`${route}/`)
  );
}

export function isSubscriptionRedirectError(error) {
  const data = error?.response?.data || {};
  return data.redirect === "/payment" && data.message === SUBSCRIPTION_REDIRECT_MESSAGE;
}

function isUnsafeMethod(method) {
  const m = String(method || "get").toLowerCase();
  return ["post", "put", "patch", "delete"].includes(m);
}

export function getCachedCsrfToken() {
  return csrfToken;
}

export async function fetchCsrfToken() {
  if (csrfToken) return csrfToken;

  if (!csrfPromise) {
    csrfPromise = axios
      .get("/api/csrf-token", {
        withCredentials: true,
      })
      .then((res) => {
        const token = res?.data?.csrfToken;

        if (!token) {
          throw new Error("Сервер не вернул csrfToken");
        }

        csrfToken = token;
        return token;
      })
      .finally(() => {
        csrfPromise = null;
      });
  }

  return csrfPromise;
}

export async function preloadCsrfToken() {
  try {
    return await fetchCsrfToken();
  } catch (error) {
    console.error("Не удалось заранее получить csrfToken", error);
    return null;
  }
}

export function resetCsrfToken() {
  csrfToken = null;
}

http.interceptors.request.use(
  async (config) => {
    if (isUnsafeMethod(config.method)) {
      const token = await fetchCsrfToken();

      config.headers = config.headers || {};
      config.headers["X-CSRF-Token"] = token;
    }

    return config;
  },
  (error) => Promise.reject(error)
);

http.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error?.config;
    const status = error?.response?.status;
    const serverMessage =
      error?.response?.data?.error ||
      error?.response?.data?.message ||
      "";

    const isCsrfError =
      status === 403 &&
      /csrf|xsrf/i.test(String(serverMessage));

    if (isSubscriptionRedirectError(error) && isSubscriptionFreeRoute()) {
      error.isSubscriptionFreeIgnored = true;
      return Promise.reject(error);
    }

    if (isCsrfError && originalRequest && !originalRequest._csrfRetry) {
      originalRequest._csrfRetry = true;

      resetCsrfToken();
      const newToken = await fetchCsrfToken();

      originalRequest.headers = originalRequest.headers || {};
      originalRequest.headers["X-CSRF-Token"] = newToken;

      return http(originalRequest);
    }

    return Promise.reject(error);
  }
);

export default http;
